import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  arrayUnion 
} from 'firebase/firestore';
import { 
  Bell, 
  Plus, 
  X, 
  Download, 
  Eye, 
  Trash2, 
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  Paperclip,
  Calendar,
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const NoticesAndAnnouncementsWidget = ({ userRole, userEmail, userName }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedAnnouncement, setExpandedAnnouncement] = useState(null);
  const [selectedStats, setSelectedStats] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    expiryDate: '',
    priority: 'normal',
    attachmentUrls: []
  });
  const [attachmentInput, setAttachmentInput] = useState('');

  const isHOD = userRole === 'hod';

  // Debug props on component mount
  useEffect(() => {
    console.log('=== NoticesAndAnnouncementsWidget Props ===');
    console.log('userRole:', userRole);
    console.log('userEmail:', userEmail);
    console.log('userName:', userName);
    console.log('isHOD:', isHOD);
    console.log('==========================================');
  }, []);

  useEffect(() => {
    if (!userRole || !userEmail) {
      console.warn('Missing required props! userRole:', userRole, 'userEmail:', userEmail);
      setLoading(false);
      setError('Unable to load announcements. User information is missing.');
      return;
    }
    fetchAnnouncements();
  }, [userRole, userEmail]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    setError(null);
    
    // Safety timeout - if loading takes more than 10 seconds, show error
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError('Loading timeout. Please check your connection and try again.');
    }, 10000);
    
    try {
      const announcementsRef = collection(db, 'announcements');
      const now = new Date();
      
      console.log('Fetching announcements for role:', userRole, 'isHOD:', isHOD);
      
      // Get all announcements first (simpler query, no indexing issues)
      const snapshot = await getDocs(announcementsRef);
      
      console.log('Total announcements in database:', snapshot.size);
      
      let data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        expiryDate: doc.data().expiryDate || new Date().toISOString()
      }));

      // Filter and sort on client side
      if (!isHOD) {
        // Non-HOD: Only show non-expired announcements
        data = data.filter(ann => new Date(ann.expiryDate) > now);
        console.log('After filtering expired (non-HOD):', data.length);
      }
      
      // Sort by created date (newest first)
      data.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });

      console.log('Final announcements to display:', data.length);
      
      clearTimeout(timeoutId); // Clear timeout on success
      setAnnouncements(data);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      console.error('Error details:', err.message, err.code);
      clearTimeout(timeoutId);
      setError(`Failed to load announcements: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const announcementsRef = collection(db, 'announcements');
      
      // Calculate expiry date (30 days from now if not specified)
      const expiryDate = formData.expiryDate 
        ? new Date(formData.expiryDate).toISOString()
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      
      await addDoc(announcementsRef, {
        title: formData.title.trim(),
        message: formData.message.trim(),
        priority: formData.priority,
        expiryDate: expiryDate,
        attachments: formData.attachmentUrls,
        createdBy: userEmail,
        createdByName: userName,
        createdAt: serverTimestamp(),
        readBy: []
      });

      setFormData({
        title: '',
        message: '',
        expiryDate: '',
        priority: 'normal',
        attachmentUrls: []
      });
      setShowCreateForm(false);
      
      // Show success feedback
      alert('Announcement created successfully!');
      
      fetchAnnouncements();
    } catch (err) {
      console.error('Error creating announcement:', err);
      alert('Failed to create announcement. Please try again.');
    }
  };

  const handleMarkAsRead = async (announcementId) => {
    if (isHOD) return;

    try {
      const announcementRef = doc(db, 'announcements', announcementId);
      await updateDoc(announcementRef, {
        readBy: arrayUnion({
          userEmail,
          userName,
          readAt: new Date().toISOString()
        })
      });
      fetchAnnouncements();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await deleteDoc(doc(db, 'announcements', announcementId));
      fetchAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      alert('Failed to delete announcement.');
    }
  };

  const addAttachment = () => {
    if (attachmentInput.trim()) {
      setFormData({
        ...formData,
        attachmentUrls: [...formData.attachmentUrls, { name: attachmentInput, url: attachmentInput }]
      });
      setAttachmentInput('');
    }
  };

  const removeAttachment = (index) => {
    setFormData({
      ...formData,
      attachmentUrls: formData.attachmentUrls.filter((_, i) => i !== index)
    });
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  const isRead = (announcement) => {
    return announcement.readBy?.some(reader => reader.userEmail === userEmail);
  };

  const getReadStats = (announcement) => {
    const totalReaders = announcement.readBy?.length || 0;
    return {
      totalReaders,
      readers: announcement.readBy || []
    };
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'normal': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <AlertCircle className="w-4 h-4" />;
      case 'high': return <TrendingUp className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading announcements...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 w-full">
      <div className="flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Notices & Announcements
              </h3>
              <p className="text-sm text-gray-500">
                {isHOD ? 'Manage and track announcements' : 'Stay updated with latest notices'}
              </p>
            </div>
          </div>

          {isHOD && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-md transition-all"
            >
              {showCreateForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              <span>{showCreateForm ? 'Cancel' : 'New Notice'}</span>
            </button>
          )}
        </div>

        {/* Stats Banner for HOD */}
        {isHOD && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-600 mb-1">Total Notices</div>
                <div className="text-2xl font-bold text-indigo-600">{announcements.length}</div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Active</div>
                <div className="text-2xl font-bold text-green-600">
                  {announcements.filter(a => !isExpired(a.expiryDate)).length}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Expired</div>
                <div className="text-2xl font-bold text-red-600">
                  {announcements.filter(a => isExpired(a.expiryDate)).length}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 mb-1">Avg Engagement</div>
                <div className="text-2xl font-bold text-purple-600">
                  {announcements.length > 0 
                    ? Math.round((announcements.reduce((sum, a) => sum + (a.readBy?.length || 0), 0) / announcements.length))
                    : 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Form */}
        {isHOD && showCreateForm && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <form onSubmit={handleCreateAnnouncement} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter announcement title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Enter announcement message"
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Attachments (URLs)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={attachmentInput}
                    onChange={(e) => setAttachmentInput(e.target.value)}
                    placeholder="Enter attachment URL"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={addAttachment}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Add
                  </button>
                </div>
                {formData.attachmentUrls.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {formData.attachmentUrls.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                        <span className="text-sm text-gray-700 truncate">{att.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-semibold"
                >
                  Create Announcement
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Announcements List */}
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {announcements.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 font-medium mb-2">No announcements available</p>
              {isHOD && (
                <p className="text-sm text-gray-400">
                  Click "New Notice" above to create your first announcement
                </p>
              )}
            </div>
          ) : (
            announcements.map((announcement) => {
              const expired = isExpired(announcement.expiryDate);
              const read = isRead(announcement);
              const stats = getReadStats(announcement);
              const isExpanded = expandedAnnouncement === announcement.id;

              return (
                <div
                  key={announcement.id}
                  className={`bg-white rounded-lg border-2 transition-all ${
                    expired ? 'border-gray-200 opacity-60' : 
                    !read && !isHOD ? 'border-indigo-400 shadow-md' : 'border-gray-200'
                  }`}
                >
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(announcement.priority)}`}>
                            {getPriorityIcon(announcement.priority)}
                            {announcement.priority.toUpperCase()}
                          </span>
                          {expired && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
                              EXPIRED
                            </span>
                          )}
                          {!read && !isHOD && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                              NEW
                            </span>
                          )}
                        </div>
                        <h4 className="text-lg font-bold text-gray-800">{announcement.title}</h4>
                        <p className="text-xs text-gray-500 mt-1">
                          By {announcement.createdByName} • {announcement.createdAt.toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {isHOD && (
                          <button
                            onClick={() => setSelectedStats(selectedStats === announcement.id ? null : announcement.id)}
                            className="p-2 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View Statistics"
                          >
                            <Users className="w-5 h-5 text-indigo-600" />
                          </button>
                        )}
                        {isHOD && (
                          <button
                            onClick={() => handleDeleteAnnouncement(announcement.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setExpandedAnnouncement(isExpanded ? null : announcement.id);
                            if (!isExpanded && !read && !isHOD) {
                              handleMarkAsRead(announcement.id);
                            }
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Preview */}
                    {!isExpanded && (
                      <p className="text-sm text-gray-600 line-clamp-2">{announcement.message}</p>
                    )}

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-4 space-y-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{announcement.message}</p>
                        </div>

                        {announcement.attachments && announcement.attachments.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                              <Paperclip className="w-4 h-4" />
                              Attachments
                            </h5>
                            <div className="space-y-2">
                              {announcement.attachments.map((att, idx) => (
                                <a
                                  key={idx}
                                  href={att.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-indigo-400 transition-colors"
                                >
                                  <span className="text-sm text-gray-700 truncate">{att.name}</span>
                                  <Download className="w-4 h-4 text-indigo-600" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Expires: {new Date(announcement.expiryDate).toLocaleDateString()}
                          </div>
                          {isHOD && (
                            <div className="flex items-center gap-1">
                              <Eye className="w-4 h-4" />
                              {stats.totalReaders} read
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Statistics for HOD */}
                    {isHOD && selectedStats === announcement.id && (
                      <div className="mt-4 border-t pt-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" />
                          Engagement Statistics
                        </h5>
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-3">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs text-gray-600">Total Reads</div>
                              <div className="text-2xl font-bold text-indigo-600">{stats.totalReaders}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600">Engagement Rate</div>
                              <div className="text-2xl font-bold text-purple-600">
                                {stats.totalReaders > 0 ? Math.round((stats.totalReaders / 100) * 100) : 0}%
                              </div>
                            </div>
                          </div>
                        </div>
                        {stats.readers.length > 0 ? (
                          <div className="space-y-2 max-h-48 overflow-y-auto">
                            {stats.readers.map((reader, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
                                <div>
                                  <div className="text-sm font-medium text-gray-800">{reader.userName}</div>
                                  <div className="text-xs text-gray-500">{reader.userEmail}</div>
                                </div>
                                <div className="text-xs text-gray-500">
                                  <CheckCircle className="w-4 h-4 text-green-600 inline mr-1" />
                                  {new Date(reader.readAt).toLocaleString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 text-center py-4">No one has read this announcement yet</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default NoticesAndAnnouncementsWidget;